import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  TablePagination,
  Chip,
  Avatar,
  InputAdornment,
  Skeleton,
  Grid,
  IconButton,
  Tooltip as MuiTooltip,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
} from "@mui/material";
import {
  MdDelete,
  MdAdd,
  MdSearch,
  MdStar,
  MdImage,
  MdGridView,
  MdViewList,
  MdRefresh,
  MdClear,
  MdCheckCircle,
  MdOutlineDesignServices,
} from "react-icons/md";
import toast from "react-hot-toast";
import { getCatelog, updateCatelog } from "../apis/catelogApi";
import { getAllservices } from "../apis/servicesApi";
import { brand } from "../theme";

const StatCard = ({ icon, label, count, accent }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: "20px",
      display: "flex",
      alignItems: "center",
      gap: 2,
      bgcolor: brand.paper,
      border: `1px solid ${brand.slateLight}40`,
      boxShadow: "0 10px 30px -15px rgba(11,10,61,0.06)",
      transition: "transform 200ms ease, box-shadow 200ms ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 18px 40px -18px rgba(11,10,61,0.12)",
        borderColor: `${brand.brass}66`,
      },
    }}
  >
    <Box
      sx={{
        width: 46,
        height: 46,
        borderRadius: "14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: `${accent || brand.brass}18`,
        color: accent || brand.ink950,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box minWidth={0}>
      <Typography
        sx={{
          fontFamily: '"Fraunces", serif',
          fontWeight: 700,
          fontSize: "1.8rem",
          color: brand.ink950,
          lineHeight: 1.1,
        }}
      >
        {count}
      </Typography>
      <Typography sx={{ color: brand.slate, fontSize: "0.82rem", fontWeight: 600 }} noWrap>
        {label}
      </Typography>
    </Box>
  </Paper>
);

const MostBooked = () => {
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [tempSelected, setTempSelected] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");
  const [modalSearch, setModalSearch] = useState("");
  const [viewMode, setViewMode] = useState("table"); // 'table' | 'grid'
  const [isOpen, setIsOpen] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const loadData = async () => {
    setLoading(true);
    try {
      const [catelogRes, servicesRes] = await Promise.all([
        getCatelog(1).catch(() => []),
        getAllservices().catch(() => []),
      ]);

      const rawProducts = catelogRes[0]?.products;
      const list = rawProducts ? rawProducts.split(",").map((s) => s.trim()) : [];
      setSelectedProducts(list);
      setTempSelected(list);
      setServices(Array.isArray(servicesRes) ? servicesRes : []);
    } catch (err) {
      toast.error("Failed to load catalog data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  /* ── Filtered Selected Services Objects ── */
  const selectedServiceObjects = useMemo(() => {
    return selectedProducts
      .map((idStr) => services.find((s) => String(s.id) === String(idStr)))
      .filter(Boolean);
  }, [selectedProducts, services]);

  const filteredSelectedServices = useMemo(() => {
    if (!search.trim()) return selectedServiceObjects;
    const q = search.toLowerCase();
    return selectedServiceObjects.filter((s) => {
      const name = (s.name || "").toLowerCase();
      const desc = (s.description || "").toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [selectedServiceObjects, search]);

  const paginatedServices = useMemo(() => {
    return filteredSelectedServices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredSelectedServices, page, rowsPerPage]);

  /* ── Modal Filtered Services List ── */
  const modalFilteredServices = useMemo(() => {
    if (!modalSearch.trim()) return services;
    const q = modalSearch.toLowerCase();
    return services.filter(
      (s) => (s.name || "").toLowerCase().includes(q) || (s.description || "").toLowerCase().includes(q)
    );
  }, [services, modalSearch]);

  const stats = useMemo(() => {
    const totalSelected = selectedProducts.length;
    const totalServices = services.length;
    const ratio = totalServices ? Math.round((totalSelected / totalServices) * 100) : 0;

    let totalPriceSum = 0;
    selectedServiceObjects.forEach((s) => {
      totalPriceSum += Number(s.price || 0);
    });
    const avgPrice = totalSelected ? Math.round(totalPriceSum / totalSelected) : 0;

    return {
      totalSelected,
      totalServices,
      ratio,
      avgPrice,
    };
  }, [selectedProducts, services, selectedServiceObjects]);

  /* ------------------ HANDLERS ------------------ */

  const handleOpen = () => {
    setTempSelected([...selectedProducts]);
    setModalSearch("");
    setIsOpen(true);
  };

  const handleToggleProduct = (productIdStr) => {
    setTempSelected((prev) =>
      prev.includes(productIdStr)
        ? prev.filter((p) => p !== productIdStr)
        : [...prev, productIdStr]
    );
  };

  const handleSelectAllInModal = () => {
    const allIds = services.map((s) => String(s.id));
    setTempSelected(allIds);
  };

  const handleDeselectAllInModal = () => {
    setTempSelected([]);
  };

  const handleSubmit = async () => {
    setSaving(true);
    const payload = {
      products: tempSelected.length ? tempSelected.join(",") : null,
    };

    try {
      await updateCatelog(1, payload);
      toast.success("Most Booked services updated successfully");
      setSelectedProducts([...tempSelected]);
      setIsOpen(false);
    } catch (err) {
      toast.error("Failed to update catalog selection");
    } finally {
      setSaving(false);
    }
  };

  const removeProduct = async (productIdStr) => {
    const updated = selectedProducts.filter((p) => p !== productIdStr);
    const payload = {
      products: updated.length ? updated.join(",") : null,
    };

    try {
      await updateCatelog(1, payload);
      toast.success("Featured service removed");
      setSelectedProducts(updated);
      setTempSelected(updated);
    } catch (err) {
      toast.error("Failed to remove featured service");
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Box p={{ xs: 2, sm: 3, md: 4 }} sx={{ maxWidth: 1600, margin: "0 auto" }}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        gap={2}
        mb={3}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Typography
              variant="h3"
              sx={{
                color: brand.ink950,
                fontFamily: '"Fraunces", serif',
                fontWeight: 700,
                fontSize: { xs: "1.8rem", md: "2.2rem" },
              }}
            >
              Most Booked Services
            </Typography>
            <Chip
              label={`${selectedProducts.length} Featured Offerings`}
              size="small"
              sx={{ bgcolor: brand.brassSoft, color: brand.ink950, fontWeight: 700 }}
            />
          </Box>
          <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mt: 0.5 }}>
            Manage trending and featured home services highlighted on customer booking portals
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <MuiTooltip title="Reload showcase items">
            <IconButton
              onClick={loadData}
              sx={{
                bgcolor: brand.paper,
                border: `1px solid ${brand.slateLight}60`,
                p: 1.1,
                borderRadius: "14px",
                color: brand.ink950,
                "&:hover": { bgcolor: brand.brassSoft },
              }}
            >
              <MdRefresh size={20} className={loading ? "animate-spin" : ""} />
            </IconButton>
          </MuiTooltip>

          <Button
            variant="contained"
            color="secondary"
            onClick={handleOpen}
            startIcon={<MdStar size={20} />}
            sx={{
              px: 2.5,
              py: 1,
              borderRadius: "14px",
              fontWeight: 700,
              fontSize: "0.88rem",
              boxShadow: "0 8px 20px -6px rgba(201,162,39,0.5)",
            }}
          >
            Manage Featured Selection
          </Button>
        </Box>
      </Box>

      {/* ── Stat Cards Grid ─────────────────────────────────────────────── */}
      <Grid container spacing={2.5} mb={3.5}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<MdStar size={24} />} label="Featured Count" count={stats.totalSelected} accent={brand.brass} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<MdOutlineDesignServices size={24} />}
            label="Catalog Coverage Ratio"
            count={`${stats.ratio}%`}
            accent="#3B82F6"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<MdCheckCircle size={24} />}
            label="Average Base Price"
            count={`₹${stats.avgPrice.toLocaleString("en-IN")}`}
            accent="#10B981"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<MdOutlineDesignServices size={24} />}
            label="Total Available Catalog"
            count={stats.totalServices}
            accent={brand.ink950}
          />
        </Grid>
      </Grid>

      {/* ── Filter Controls Bar ─────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "20px",
          bgcolor: brand.paper,
          border: `1px solid ${brand.slateLight}40`,
          mb: 3,
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          gap={2}
        >
          {/* Search Bar */}
          <TextField
            placeholder="Search featured services..."
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{
              width: { xs: "100%", sm: 340 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
                bgcolor: `${brand.slateLight}15`,
                fontSize: "0.85rem",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch color={brand.slate} size={20} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <MdClear size={16} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* View Switcher */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            size="small"
            sx={{
              bgcolor: `${brand.slateLight}20`,
              p: 0.4,
              borderRadius: "12px",
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "9px",
                px: 1.5,
                py: 0.5,
                color: brand.slate,
                "&.Mui-selected": {
                  bgcolor: brand.paper,
                  color: brand.ink950,
                  boxShadow: "0 2px 8px rgba(11,10,61,0.08)",
                },
              },
            }}
          >
            <ToggleButton value="table">
              <MdViewList size={18} />
            </ToggleButton>
            <ToggleButton value="grid">
              <MdGridView size={18} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {/* ── Table vs Grid View ─────────────────────────────────────────── */}
      {viewMode === "table" ? (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: "22px",
            overflow: "hidden",
            border: `1px solid ${brand.slateLight}40`,
            bgcolor: brand.paper,
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Cover Thumbnail</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Service Name</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Regular Price (₹)</TableCell>
                <TableCell sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>Status Badge</TableCell>
                <TableCell align="right" sx={{ bgcolor: brand.ink950, color: brand.parchment, fontWeight: 700 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: rowsPerPage }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" height={28} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedServices.length > 0 ? (
                paginatedServices.map((srv, index) => (
                  <TableRow
                    key={srv.id || index}
                    sx={{ "&:hover": { bgcolor: `${brand.brassSoft}60` } }}
                  >
                    <TableCell
                      sx={{
                        color: brand.slate,
                        fontFamily: '"IBM Plex Mono", monospace',
                        fontSize: "0.82rem",
                        fontWeight: 600,
                      }}
                    >
                      {String(page * rowsPerPage + index + 1).padStart(2, "0")}
                    </TableCell>
                    <TableCell>
                      {srv.image ? (
                        <Box
                          component="img"
                          src={`http://skishore.in/api/public/${srv.image}`}
                          alt={srv.name}
                          sx={{
                            width: 54,
                            height: 40,
                            borderRadius: "10px",
                            objectFit: "cover",
                            border: `1px solid ${brand.slateLight}50`,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 54,
                            height: 40,
                            borderRadius: "10px",
                            bgcolor: `${brand.slateLight}30`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: brand.slate,
                          }}
                        >
                          <MdImage size={20} />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.95rem", color: brand.ink950 }}>
                      {srv.name}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: brand.ink950, fontSize: "0.92rem" }}>
                      ₹{Number(srv.price || 0).toLocaleString("en-IN")}
                      {srv.salePrice && (
                        <Typography component="span" sx={{ fontSize: "0.72rem", color: brand.danger, ml: 1, textDecoration: "line-through" }}>
                          ₹{srv.salePrice}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<MdStar size={14} />}
                        label="Featured Most Booked"
                        size="small"
                        sx={{
                          bgcolor: brand.brassSoft,
                          color: brand.ink950,
                          fontWeight: 700,
                          fontSize: "0.72rem",
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <MuiTooltip title="Remove from Most Booked">
                        <IconButton
                          size="small"
                          onClick={() => removeProduct(String(srv.id))}
                          sx={{ bgcolor: `${brand.danger}15`, color: brand.danger }}
                        >
                          <MdDelete size={16} />
                        </IconButton>
                      </MuiTooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <MdStar size={40} color={brand.slateLight} />
                    <Typography
                      sx={{
                        fontFamily: '"Fraunces", serif',
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        color: brand.ink950,
                        mt: 1,
                      }}
                    >
                      No featured services selected
                    </Typography>
                    <Typography sx={{ color: brand.slate, fontSize: "0.85rem", mt: 0.5 }}>
                      {search
                        ? "Try a different search query."
                        : "Click 'Manage Featured Selection' to select popular services for the homepage."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredSelectedServices.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </TableContainer>
      ) : (
        /* Grid View */
        <Grid container spacing={3} mb={3}>
          {paginatedServices.map((srv) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={srv.id}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "22px",
                  bgcolor: brand.paper,
                  border: `1px solid ${brand.slateLight}40`,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transition: "transform 200ms ease, box-shadow 200ms ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 18px 40px -15px rgba(11,10,61,0.12)",
                    borderColor: `${brand.brass}66`,
                  },
                }}
              >
                <Box position="relative" height={140} bgcolor={`${brand.ink950}10`}>
                  {srv.image ? (
                    <Box
                      component="img"
                      src={`http://skishore.in/api/public/${srv.image}`}
                      alt={srv.name}
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%" color={brand.slate}>
                      <MdImage size={36} />
                    </Box>
                  )}
                  <Box position="absolute" top={10} right={10}>
                    <Chip
                      icon={<MdStar size={14} />}
                      label="Featured"
                      size="small"
                      sx={{ bgcolor: brand.brass, color: brand.ink950, fontWeight: 700 }}
                    />
                  </Box>
                </Box>

                <Box p={2.5}>
                  <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: brand.ink950 }} noWrap>
                    {srv.name}
                  </Typography>

                  <Box display="flex" alignItems="baseline" justifyContent="space-between" mt={2} pt={1.5} borderTop={`1px solid ${brand.slateLight}30`}>
                    <Typography sx={{ fontSize: "0.75rem", color: brand.slate, fontWeight: 600 }}>Price</Typography>
                    <Typography sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.25rem", color: brand.ink950 }}>
                      ₹{Number(srv.price || 0).toLocaleString("en-IN")}
                    </Typography>
                  </Box>
                </Box>

                <Box p={2} pt={0}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => removeProduct(String(srv.id))}
                    startIcon={<MdDelete />}
                    sx={{ borderRadius: "10px" }}
                  >
                    Remove Featured
                  </Button>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Dialog: Manage Featured Services Selection Drawer ──────────── */}
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "24px", p: 1 } }}
      >
        <DialogTitle sx={{ fontFamily: '"Fraunces", serif', fontWeight: 700, fontSize: "1.35rem" }}>
          Configure Featured Most Booked Services
        </DialogTitle>

        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems="center" gap={2}>
              <TextField
                placeholder="Search catalog services..."
                size="small"
                value={modalSearch}
                onChange={(e) => setModalSearch(e.target.value)}
                sx={{
                  width: { xs: "100%", sm: 300 },
                  "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MdSearch color={brand.slate} size={18} />
                    </InputAdornment>
                  ),
                }}
              />

              <Box display="flex" gap={1}>
                <Button size="small" onClick={handleSelectAllInModal} sx={{ fontSize: "0.78rem", fontWeight: 700 }}>
                  Select All
                </Button>
                <Button size="small" color="error" onClick={handleDeselectAllInModal} sx={{ fontSize: "0.78rem", fontWeight: 700 }}>
                  Deselect All
                </Button>
              </Box>
            </Box>

            <Typography sx={{ fontSize: "0.8rem", color: brand.slate, fontWeight: 600 }}>
              {tempSelected.length} of {services.length} services selected for Featured Showcase
            </Typography>

            <Paper
              elevation={0}
              sx={{
                maxHeight: 380,
                overflowY: "auto",
                border: `1px solid ${brand.slateLight}40`,
                borderRadius: "16px",
                p: 1,
              }}
            >
              <Grid container spacing={1}>
                {modalFilteredServices.map((srv) => {
                  const isChecked = tempSelected.includes(String(srv.id));
                  return (
                    <Grid item xs={12} sm={6} key={srv.id}>
                      <Paper
                        elevation={0}
                        onClick={() => handleToggleProduct(String(srv.id))}
                        sx={{
                          p: 1.5,
                          borderRadius: "14px",
                          border: `1.5px solid ${isChecked ? brand.brass : `${brand.slateLight}40`}`,
                          bgcolor: isChecked ? brand.brassSoft : brand.paper,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          transition: "all 150ms ease",
                          "&:hover": { borderColor: brand.brass },
                        }}
                      >
                        <Checkbox
                          checked={isChecked}
                          color="secondary"
                          sx={{ p: 0.5 }}
                        />
                        <Box minWidth={0} flex={1}>
                          <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", color: brand.ink950 }} noWrap>
                            {srv.name}
                          </Typography>
                          <Typography sx={{ fontSize: "0.78rem", color: brand.slate, fontWeight: 600 }}>
                            ₹{Number(srv.price || 0).toLocaleString("en-IN")}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setIsOpen(false)} sx={{ color: brand.slate, fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="secondary"
            disabled={saving}
            sx={{ fontWeight: 700, borderRadius: "10px", px: 3 }}
          >
            {saving ? "Saving Selection..." : "Save Selection"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MostBooked;
